import {
  Buildings,
  ChevronDownMini,
  CogSixTooth,
  CurrencyDollar,
  MagnifyingGlass,
  MinusMini,
  ReceiptPercent,
  ShoppingCart,
  Tag,
  Users,
  Component,
  Star,
  ListCheckbox,
  ChatBubbleLeftRight,
  Bolt,
  DocumentText,
  Receipt,
  SquareTwoStack,
  DocumentSeries,
  WandSparkle,
  Phone,
} from "@medusajs/icons"
import { Divider, Text, clx } from "@medusajs/ui"
import { Collapsible as RadixCollapsible } from "radix-ui"
import { useTranslation } from "react-i18next"

import { Skeleton } from "../../common/skeleton"
import { INavItem, NavItem } from "../../layout/nav-item"
import { Shell } from "../../layout/shell"

import { useLocation } from "react-router-dom"
import { useMe } from "../../../hooks/api"

import { useSearch } from "../../../providers/search-provider"
import { UserMenu } from "../user-menu"
import { StripeIcon } from "../../../assets/icons/Stripe"
import { ImageAvatar } from "../../common/image-avatar"
import { useUnreads } from "@talkjs/react"
import { useSellerCapabilities } from "../../../hooks/use-seller-capabilities"
import { hasDistributorAccess } from "../../../lib/vendor-types"

export const MainLayout = () => {
  return (
    <Shell>
      <MainSidebar />
    </Shell>
  )
}

const MainSidebar = () => {
  return (
    <aside className="flex flex-1 flex-col justify-between overflow-y-auto">
      <div className="flex flex-1 flex-col">
        <div className="bg-ui-bg-subtle sticky top-0">
          <Header />
          <div className="px-3">
            <Divider variant="dashed" />
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="flex flex-1 flex-col">
            <CoreRouteSection />
            <ExtensionRouteSection />
          </div>
          <UtilitySection />
        </div>
        <div className="bg-ui-bg-subtle sticky bottom-0">
          <UserSection />
        </div>
      </div>
    </aside>
  )
}

const Header = () => {
  const { seller } = useMe()

  const name = seller?.name || ""
  const fallback = seller?.photo || "M"

  return (
    <div className="w-full p-3 p-0.5 pr-2 bg-ui-bg-subtle grid w-full grid-cols-[24px_1fr_15px] items-center gap-x-3">
      {fallback ? (
        <div className="w-7 h-7">
          <ImageAvatar src={seller?.photo || "/logo.svg"} size={7} rounded />
        </div>
      ) : (
        <Skeleton className="h-6 w-6 rounded-md" />
      )}
      <div className="block overflow-hidden text-left">
        {name ? (
          <Text
            size="small"
            weight="plus"
            leading="compact"
            className="truncate"
          >
            {name}
          </Text>
        ) : (
          <Skeleton className="h-[9px] w-[120px]" />
        )}
      </div>
    </div>
  )
}

export const useCoreRoutes = (): Omit<INavItem, "pathname">[] => {
  const { t } = useTranslation()
  const { seller } = useMe()

  const { canCreateProducts, canCreateDigitalProducts } =
    useSellerCapabilities()

  const unreadMessages = useUnreads()
  const canAccessDistributor = hasDistributorAccess(
    (seller as { vendor_type?: unknown } | undefined)?.vendor_type
  )

  return [
    {
      icon: <Tag />,
      label: t("products.domain"),
      to: "/products",
      items: [
        ...(canCreateProducts
          ? [
              {
                label: t("products.create.title"),
                to: "/products/create",
              },
            ]
          : []),
        ...(canCreateProducts && canCreateDigitalProducts
          ? [
              {
                label: t("products.albumCreate.title"),
                to: "/products/create-album",
              },
              {
                label: t("products.bulkAudio.title"),
                to: "/products/bulk-audio",
              },
            ]
          : []),
        {
          label: t("collections.domain"),
          to: "/collections",
        },
        {
          label: t("categories.domain"),
          to: "/categories",
        },
        {
          label: "Applications",
          to: "/products/applications",
        },
        {
          label: "Catalog children",
          to: "/catalog/children",
        },
        ...(canCreateProducts
          ? [
              {
                label: "Import Specs",
                to: "/pdf-catalogue",
              },
            ]
          : []),
        // TODO: Enable when domin is introduced
        // {
        //   label: t("giftCards.domain"),
        //   to: "/gift-cards",
        // },
      ],
    },
    {
      icon: <ShoppingCart />,
      label: t("orders.domain"),
      to: "/orders",
      items: [
        // TODO: Enable when domin is introduced
        // {
        //   label: t("draftOrders.domain"),
        //   to: "/draft-orders",
        // },
      ],
    },
    {
      icon: <SquareTwoStack />,
      label: "Sample Requests",
      to: "/sample-orders",
    },
    {
      icon: <WandSparkle />,
      label: "AI content staging",
      to: "/ai-product-content",
    },
    {
      icon: <Phone />,
      label: "Payment calls",
      to: "/payment-calls",
      items: [
        { label: "Scripts", to: "/payment-calls/scripts" },
        { label: "Settings", to: "/payment-calls/settings" },
        { label: "Report", to: "/payment-calls/report" },
      ],
    },
    {
      icon: <Bolt />,
      label: "Auctions",
      to: "/auctions",
    },
    {
      icon: <Users />,
      label: t("customers.domain"),
      to: "/customers",
      items: [
        {
          label: t("customerGroups.domain"),
          to: "/customer-groups",
        },
      ],
    },
    {
      icon: <Component />,
      label: "Dashboard",
      to: "/dashboard",
    },
    ...(canAccessDistributor
      ? [
          {
            icon: <SquareTwoStack />,
            label: "Distributor",
            to: "/distributors",
            items: [
              { label: "Distributors", to: "/distributors" },
              { label: "Distributor price lists", to: "/distributor/price-lists" },
              { label: "Available products", to: "/distributor/available-products" },
            ],
          },
        ]
      : []),
    {
      icon: <Users />,
      label: t("escalationManager.domain"),
      to: "/escalation-manager",
    },
    {
      icon: <Buildings />,
      label: t("inventory.domain"),
      to: "/inventory",
      items: [
        {
          label: t("reservations.domain"),
          to: "/reservations",
        },
      ],
    },
    {
      icon: <DocumentSeries />,
      label: "Legal Policies",
      to: "/legal",
    },
    {
      icon: <ChatBubbleLeftRight />,
      label: `Messages ${unreadMessages?.length && unreadMessages?.length > 0 ? `(${unreadMessages?.length})` : ""}`,
      to: "/messages",
    },
    
    {
      icon: <CurrencyDollar />,
      label: t("priceLists.domain"),
      to: "/price-lists",
    },
    {
      icon: <ReceiptPercent />,
      label: t("promotions.domain"),
      to: "/promotions",
      items: [
        {
          label: t("campaigns.domain"),
          to: "/campaigns",
        },
      ],
    },
    {
      icon: <DocumentText />,
      label: "Quotes",
      to: "/quotes",
    },
    {
      icon: <Receipt />,
      label: "Purchase Orders",
      to: "/purchase-orders",
    },
    {
      icon: <ListCheckbox />,
      label: "Requests",
      to: "/requests",
      items: [
        {
          label: "Product requests",
          to: "/requests/products",
        },
        {
          label: "Product requests (sent)",
          to: "/requests/products/outgoing",
        },
        {
          label: "Collections",
          to: "/requests/collections",
        },
        {
          label: "Categories",
          to: "/requests/categories",
        },
        {
          label: "Reviews",
          to: "/requests/reviews",
        },
        {
          label: "Orders returns",
          to: "/requests/orders",
        },
        {
          label: "Applications",
          to: "/requests/applications",
        },
      ],
    },
    {
      icon: <Star />,
      label: "Reviews",
      to: "/reviews",
    },
    {
      icon: <Users />,
      label: t("salesManagers.domain"),
      to: "/sales-managers",
    },
  ]
}

const useExtensionRoutes = (): Omit<INavItem, "pathname">[] => {
  return [
    {
      icon: <StripeIcon />,
      label: "Stripe Connect",
      to: "/stripe-connect",
    },
  ]
}

const Searchbar = () => {
  const { t } = useTranslation()
  const { toggleSearch } = useSearch()

  return (
    <div className="px-3">
      <button
        onClick={toggleSearch}
        className={clx(
          "bg-ui-bg-subtle text-ui-fg-subtle flex w-full items-center gap-x-2.5 rounded-md px-2 py-1 outline-none",
          "hover:bg-ui-bg-subtle-hover",
          "focus-visible:shadow-borders-focus"
        )}
      >
        <MagnifyingGlass />
        <div className="flex-1 text-left">
          <Text size="small" leading="compact" weight="plus">
            {t("app.search.label")}
          </Text>
        </div>
        <Text size="small" leading="compact" className="text-ui-fg-muted">
          ⌘K
        </Text>
      </button>
    </div>
  )
}

const CoreRouteSection = () => {
  const coreRoutes = useCoreRoutes()

  return (
    <nav className="flex flex-col gap-y-1 py-3">
      <Searchbar />
      {coreRoutes.map((route) => {
        return <NavItem key={route.to} {...route} />
      })}
    </nav>
  )
}

const ExtensionRouteSection = () => {
  const extensionRoutes = useExtensionRoutes()
  const { t } = useTranslation()

  if (!extensionRoutes.length) { return null }

  return (
    <div>
      <div className="px-3">
        <Divider variant="dashed" />
      </div>
      <div className="flex flex-col gap-y-1 py-3">
        <RadixCollapsible.Root defaultOpen>
          <div className="px-4">
            <RadixCollapsible.Trigger asChild className="group/trigger">
              <button className="text-ui-fg-subtle flex w-full items-center justify-between px-2">
                <Text size="xsmall" weight="plus" leading="compact">
                  {t("app.nav.common.extensions")}
                </Text>
                <div className="text-ui-fg-muted">
                  <ChevronDownMini className="group-data-[state=open]/trigger:hidden" />
                  <MinusMini className="group-data-[state=closed]/trigger:hidden" />
                </div>
              </button>
            </RadixCollapsible.Trigger>
          </div>
          <RadixCollapsible.Content>
            <nav className="flex flex-col gap-y-0.5 py-1 pb-4">
              {extensionRoutes.map((route) => {
                return <NavItem key={route.to} {...route} />
              })}
            </nav>
          </RadixCollapsible.Content>
        </RadixCollapsible.Root>
      </div>
    </div>
  )
}

const UtilitySection = () => {
  const location = useLocation()
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-y-0.5 py-3">
      <NavItem
        label={t("app.nav.settings.header")}
        to="/settings"
        from={location.pathname}
        icon={<CogSixTooth />}
      />
    </div>
  )
}

const UserSection = () => {
  return (
    <div>
      <div className="px-3">
        <Divider variant="dashed" />
      </div>
      <UserMenu />
    </div>
  )
}
